import {noop} from '@benchling/js-utils';
import {Textarea} from '@mantine/core';
import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React, {useState, useRef, useCallback} from 'react';
import _ from 'underscore';

import {getInsufficientCreditsAgentTypeError} from 'llm/chat-library/MeteringUtils';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import {AIModalSelectedFile} from 'notebook/ai-tools/AIModalFileChooser';
import {
  suggestDataEntryClarifications,
  SuggestDataEntryClarificationsResponse,
} from 'notebook/ai-tools/NotebookAIToolsDAO';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {StructuredTable} from 'notebook/structured-tables/StructuredTableConstants';
import {FONT_PROXIMA_13, FONT_PROXIMA_TITLE, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {RED} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ModalButton} from 'ui-library/modals/Modal';
import * as ImportUtil from 'util/ImportUtil';

import isStructuredTableColumnFillable from './isStructuredTableColumnFillable';

export enum SuggestedClarificationsStateType {
  UNSET = 'UNSET',
  UPDATING = 'UPDATING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

export type AIDataEntrySuggestedClarificationsState =
  | {type: SuggestedClarificationsStateType.UNSET; suggestedPrompt: string}
  | {type: SuggestedClarificationsStateType.UPDATING; suggestedPrompt: string}
  | {
      type: SuggestedClarificationsStateType.SUCCESS;
      response: SuggestDataEntryClarificationsResponse;
      userSpecifiedRows: {[tableName: string]: Array<{text: string}>} | null;
      suggestedPrompt: string;
    }
  | {type: SuggestedClarificationsStateType.FAILURE; suggestedPrompt: string};

export function getSuggestClarificationsModalButtons({
  handleSubmit,
  handleGoBack,
  isUpdating,
  hasBlockingBanner,
}: {
  handleSubmit: () => void;
  handleGoBack: () => void;
  isUpdating: boolean;
  hasBlockingBanner: boolean;
}): Array<ModalButton> {
  return [
    {
      text: 'Discard and go back',
      style: ButtonStyle.TERTIARY_GRAY,
      onClick: handleGoBack,
      disabled: isUpdating,
    },
    {
      text: 'Submit',
      style: ButtonStyle.PRIMARY,
      onClick: handleSubmit,
      disabled: isUpdating || hasBlockingBanner,
      dataTestAttrs: {component: 'AIFillStructuredTableModal', element: 'submitButton'},
    },
  ];
}

/**
 * State and actions for the suggested clarifications system.
 */
export interface AIDataEntrySuggestedClarificationsControls {
  suggestedClarificationsState: AIDataEntrySuggestedClarificationsState;
  setSuggestedClarificationsState: (state: AIDataEntrySuggestedClarificationsState) => void;
  suggestClarifications: (
    entryId: string,
    tableNames: Array<string>,
    selectedFiles: Array<AIModalSelectedFile>,
    promptText: string,
    includeSuggestedPrompt?: boolean
  ) => Promise<void>;
  clearSuggestedClarifications: () => void;
}

/**
 * Custom hook for managing both server and client state for suggested clarifications.
 */
export function useAIDataEntrySuggestedClarificationsControls(
  initialPromptText: string,
  setBlockingBannerMessage: (message: {title: string; detail: string} | null) => void
): AIDataEntrySuggestedClarificationsControls {
  const [suggestedClarificationsState, setSuggestedClarificationsState] =
    useState<AIDataEntrySuggestedClarificationsState>({
      type: SuggestedClarificationsStateType.UNSET,
      suggestedPrompt: initialPromptText,
    });
  // Track unique request IDs to properly account for concurrent requests.
  const latestRequestIdRef = useRef<string | null>(null);

  const suggestClarifications = useCallback(
    async (
      entryId: string,
      tableNames: Array<string>,
      selectedFiles: Array<AIModalSelectedFile>,
      promptText: string,
      isInitialSuggestion: boolean
    ): Promise<void> => {
      const requestId = _.uniqueId('suggest-clarifications');
      latestRequestIdRef.current = requestId;

      if (!isInitialSuggestion) {
        setSuggestedClarificationsState({
          type: SuggestedClarificationsStateType.UPDATING,
          suggestedPrompt: promptText,
        });
      }
      let response: SuggestDataEntryClarificationsResponse;
      try {
        const files = await Promise.all(
          selectedFiles.map(async ({file}) => ({
            s3Location: (await ImportUtil.upload(file))!,
            mediaType: file.type,
          }))
        );
        response = await suggestDataEntryClarifications(
          entryId,
          tableNames,
          files,
          promptText,
          // only request a suggested prompt for initial suggestion
          isInitialSuggestion
        );
      } catch (e) {
        if (latestRequestIdRef.current === requestId) {
          const creditsError = getInsufficientCreditsAgentTypeError(e);
          if (creditsError) {
            (e as {__suppress_notification?: boolean}).__suppress_notification = true;
            setBlockingBannerMessage(creditsError);
            setSuggestedClarificationsState({
              type: SuggestedClarificationsStateType.FAILURE,
              suggestedPrompt: promptText,
            });
            return;
          }
          setBlockingBannerMessage(null);
          setSuggestedClarificationsState({
            type: SuggestedClarificationsStateType.FAILURE,
            suggestedPrompt: promptText,
          });
        }
        throw e;
      }
      if (latestRequestIdRef.current === requestId) {
        const userSpecifiedRows: {[tableName: string]: Array<{text: string}>} = {};
        if (response.suggestedFirstRows) {
          (response.tableOrder || tableNames).forEach((tableName, index) => {
            userSpecifiedRows[tableName] = response.suggestedFirstRows![index] || [];
          });
        }

        setSuggestedClarificationsState({
          type: SuggestedClarificationsStateType.SUCCESS,
          response,
          userSpecifiedRows,
          suggestedPrompt: isInitialSuggestion ? response.suggestedPrompt || '' : promptText,
        });
        setBlockingBannerMessage(null);
      }
    },
    [latestRequestIdRef, setBlockingBannerMessage]
  );

  const clearSuggestedClarifications = useCallback(() => {
    // Reset the request ID so that any previous requests are discarded.
    latestRequestIdRef.current = null;
    setBlockingBannerMessage(null);
    setSuggestedClarificationsState({
      type: SuggestedClarificationsStateType.UNSET,
      suggestedPrompt: initialPromptText,
    });
  }, [initialPromptText, setBlockingBannerMessage]);

  return {
    suggestedClarificationsState,
    setSuggestedClarificationsState,
    suggestClarifications,
    clearSuggestedClarifications,
  };
}

/**
 * If applicable, append the example row to the end of the user-provided prompt to create a combined
 * prompt to send to the LLM.
 */
export function getUpdatedPromptTextWithSuggestedClarifications(
  promptText: string,
  structuredTables: Array<StructuredTable<unknown, unknown>>,
  suggestedClarificationsState: AIDataEntrySuggestedClarificationsState
): string {
  if (suggestedClarificationsState.type !== SuggestedClarificationsStateType.SUCCESS) {
    return promptText;
  }
  const {userSpecifiedRows} = suggestedClarificationsState;
  if (!userSpecifiedRows) {
    return promptText;
  }
  const suffix = Object.entries(userSpecifiedRows)
    .map(
      ([tableName, userSpecifiedRow]) => `\
Example row for ${tableName}:
${userSpecifiedRow
  .flatMap((cell, i) => {
    const column = structuredTables.find((table) => table.name === tableName)?.columns[i];
    if (column && isStructuredTableColumnFillable(column)) {
      const cellText = cell.text === '' ? '[Leave blank]' : cell.text;
      return [`${column.name}: ${cellText}`];
    }
    return [];
  })
  .join('\n')}
`
    )
    .join('\n\n');
  if (promptText.trim()) {
    return `${promptText}\n\n${suffix}`;
  } else {
    return suffix;
  }
}

interface SuggestClarificationsStepProps {
  suggestedClarificationsState: AIDataEntrySuggestedClarificationsState;
  structuredTables: Array<StructuredTable<unknown, unknown>>;
  onUpdateSuggestedClarificationsState: (state: AIDataEntrySuggestedClarificationsState) => void;
  onRegenerateExampleRow: () => void;
}

/**
 * Second step of Data Entry Agent modal, where the user reviews suggested instructions and example rows.
 */
export default function SuggestClarificationsStep({
  suggestedClarificationsState,
  structuredTables,
  onUpdateSuggestedClarificationsState,
  onRegenerateExampleRow,
}: SuggestClarificationsStepProps): JSX.Element {
  const handleSetPromptText = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (suggestedClarificationsState.type === SuggestedClarificationsStateType.SUCCESS) {
        onUpdateSuggestedClarificationsState({
          ...suggestedClarificationsState,
          suggestedPrompt: e.target.value,
        });
      }
    },
    [onUpdateSuggestedClarificationsState, suggestedClarificationsState]
  );

  const generateLink = useCallback(
    (text: string) => (
      <Button
        buttonStyle={ButtonStyle.LINK}
        icon="refresh"
        text={text}
        onClick={onRegenerateExampleRow}
        textClassName={css(styles.retryText)}
      />
    ),
    [onRegenerateExampleRow]
  );

  if (suggestedClarificationsState.type === SuggestedClarificationsStateType.UNSET) {
    return (
      <div className={css(styles.loadingMessage)}>
        <LLMLoadingMessage text="Analyzing data to suggest instructions and an example row..." />
      </div>
    );
  }

  // Render instructions section
  const instructionsSection = (
    <>
      <div className={css(styles.sectionTitle)}>Confirm instructions</div>
      <div>Confirm these additional instructions on how AI system will fill the table:</div>
      <Textarea
        classNames={{input: classNames(css(styles.textArea), 'scrollbar-override-no-hover')}}
        value={suggestedClarificationsState.suggestedPrompt}
        onChange={handleSetPromptText}
        minRows={6}
      />
      <div>
        <span className={css(styles.tip)}>Tip:</span> Notebook contents are also considered, including tables
        and any instructions.
      </div>
    </>
  );

  // Render example row section
  let exampleRowSection: JSX.Element;

  if (suggestedClarificationsState.type === SuggestedClarificationsStateType.UPDATING) {
    exampleRowSection = (
      <div>
        <div className={css(styles.buttonContainer)}>
          {generateLink('Generate example row based on latest instructions')}
        </div>
        <div className={css(styles.loadingMessage)}>
          <LLMLoadingMessage text="Analyzing data to suggest an example row..." />
        </div>
      </div>
    );
  } else if (suggestedClarificationsState.type === SuggestedClarificationsStateType.FAILURE) {
    exampleRowSection = (
      <div>
        <div>
          <span className={css(styles.errorIcon)}>
            <BenchlingIcon icon="error" />
          </span>
          An error occurred while generating an example row. {generateLink('Retry')}
        </div>
      </div>
    );
  } else {
    // SUCCESS case
    const {
      response: {errorMessage, hadError},
      userSpecifiedRows,
    } = suggestedClarificationsState;

    if (hadError) {
      exampleRowSection = (
        <div>
          <div className={css(styles.buttonContainer)}>{generateLink('Retry')}</div>
          <div>
            <span className={css(styles.errorIcon)}>
              <BenchlingIcon icon="error" />
            </span>
            {errorMessage ? (
              <span>
                <span className={css(styles.errorMessage)}>Problem encountered: </span>
                {errorMessage}
              </span>
            ) : (
              <span>An error occurred while generating an example row.</span>
            )}
          </div>
        </div>
      );
    } else {
      const isFillableByColumnIndexPerTable: {[tableName: string]: {[columnIndex: number]: boolean}} = {};
      for (const structuredTable of structuredTables) {
        isFillableByColumnIndexPerTable[structuredTable.name] = {};
        for (let i = 0; i < structuredTable.columns.length; i++) {
          isFillableByColumnIndexPerTable[structuredTable.name][i] = isStructuredTableColumnFillable(
            structuredTable.columns[i]
          );
        }
      }
      exampleRowSection = (
        <div>
          <div className={css(styles.buttonContainer)}>
            {generateLink('Generate new example row based on latest instructions')}
          </div>
          <div>Review the example row below and edit to make any corrections.</div>
          {Object.entries(userSpecifiedRows || {}).map(([tableName, userSpecifiedRow], tableIndex) => {
            const structuredTable = structuredTables.find((t) => t.name === tableName);
            return (
              <div className="mediocre" key={`suggested-clarifications-${tableName}`}>
                {structuredTable ? (
                  <SimpleTableEditable
                    {...standaloneProps}
                    cid="tmp-flexible-data-entry-suggested-clarifications"
                    locked={false}
                    table={{
                      name: `Example row for ${tableName}`,
                      columnLabels: structuredTable.columns.map((c) => c.name),
                      table: [
                        userSpecifiedRow.map((cell, i) => ({
                          ...cell,
                          locked: !isFillableByColumnIndexPerTable[tableName][i],
                        })),
                      ],
                      widths: structuredTable.columns.map((c) => c.width),
                      columnConstraints: structuredTable.columns.map((c) => null),
                      allowAddRows: false,
                      lockedDims: true,
                    }}
                    hideFormulaBox={true}
                    enableFormulas={false}
                    enableFillDown={false}
                    storeState={noop}
                    updateHandler={(index, {table}) => {
                      const updatedRows = {...userSpecifiedRows};
                      updatedRows[tableName] = table.table[0];
                      onUpdateSuggestedClarificationsState({
                        type: SuggestedClarificationsStateType.SUCCESS,
                        response: suggestedClarificationsState.response,
                        userSpecifiedRows: updatedRows,
                        suggestedPrompt: suggestedClarificationsState.suggestedPrompt,
                      });
                    }}
                  />
                ) : null}
              </div>
            );
          })}
        </div>
      );
    }
  }

  return (
    <div>
      {instructionsSection}
      <div className={css(styles.sectionTitle, styles.exampleRowTitle)}>Confirm example row</div>
      <div className={css(styles.suggestedClarificationsContainer)}>{exampleRowSection}</div>
    </div>
  );
}

const styles = StyleSheet.create({
  sectionTitle: {
    ...FONT_PROXIMA_TITLE,
    marginBottom: 4,
  },
  exampleRowTitle: {
    marginTop: 15,
  },
  textArea: {
    ...FONT_PROXIMA_13,
    marginTop: 4,
    marginBottom: 4,
  },
  tip: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  suggestedClarificationsContainer: {
    marginBottom: 8,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  loadingText: {
    marginLeft: 4,
  },
  errorIcon: {
    color: RED,
    marginRight: 4,
  },
  errorMessage: {
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  retryText: {
    marginLeft: 4,
  },
  buttonContainer: {
    marginBottom: 4,
  },
  loadingMessage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    padding: '16px 0',
  },
});
